import { DialCodeEntry } from "@/components/Inputs/PhoneInput/countryDialCodes";
import { MutableRefObject, useEffect } from "react";
import { FieldValues, Path, PathValue, UseFormSetValue, UseFormTrigger } from "react-hook-form";

interface UseSyncPhoneToFormOptions<T extends FieldValues = FieldValues> {
  name: Path<T>;
  rawNumber: string;
  selectedEntry: DialCodeEntry;
  isTouched: boolean;
  isInitialized: MutableRefObject<boolean>;
  setValue: UseFormSetValue<T>;
  trigger: UseFormTrigger<T>;
}

/**
 * Writes the combined phone value (+385XXXXXXXX) into the RHF field
 * whenever the raw number or dial code changes.
 * Skips the initial mount to prevent overwriting the rehydrated value.
 */
export const useSyncPhoneToForm = <T extends FieldValues = FieldValues>({
  name,
  rawNumber,
  selectedEntry,
  isTouched,
  isInitialized,
  setValue,
  trigger,
}: UseSyncPhoneToFormOptions<T>) => {
  useEffect(() => {
    if (!isInitialized.current) return;
    const combined = rawNumber.trim() ? `${selectedEntry.dialCode}${rawNumber.trim()}` : "";
    setValue(name, combined as PathValue<T, Path<T>>, { shouldDirty: true });
    if (combined || isTouched) {
      setTimeout(() => trigger(name));
    }
  }, [selectedEntry.dialCode, rawNumber, name, setValue, trigger, isTouched, isInitialized]);
};
